<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="1000px"
    :scrollable="true"
    card-class="blue-grey lighten-5"
    small-title
  >
    <template slot="dialogTitle">
      <span>
        Set pricing and finance model
      </span>
    </template>

    <v-card-text class="pa-0">
      <v-row
        class="fill-height"
        no-gutters
      >
        <v-col
          lg="11"
          class="pa-6 fill-height"
          style="overflow-y: auto"
        >
          <!--<editor-fold desc="Finance Model">-->
          <v-select
            v-model="selectedFinanceModel"
            outlined
            label="Finance Model"
            placeholder="Select finance model"
            :items="financeModels.data"
            :loading="financeModels.loading"
            :disabled="editingDisabled"
            item-text="label"
            item-value="id"
            hide-details
            clearable
            return-object
            @input="onChangedFinanceModel"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.label }}
                </v-list-item-title>
                <v-list-item-subtitle class="caption">
                  <span v-if="item.parameters.investmentPeriodMonths">
                    {{ item.parameters.investmentPeriodMonths }} months &middot;
                  </span>
                  <span v-if="item.institution_name">
                    {{ item.institution_name }}
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-select>

          <v-expand-transition>
            <div v-if="calculator.data.current">
              <v-subheader class="mt-3">
                Parameters
              </v-subheader>

              <div
                v-if="calculator.data.current.navigare.generates_yearly_loss"
                class="error--text caption mb-2"
              >
                <div v-if="hasFirstPaymentPercentage">
                  This finance model generates a yearly loss for you on this yacht. To fix this, please:<br>
                  1. Apply discount on the final price<br>
                  2. Apply the suggested parameters
                </div>
                <div v-else-if="calculator.data.current.investor.guaranteed_incomes">
                  The guaranteed yearly income to the Yacht Owner generates a yearly loss for you.
                </div>
                <div v-else>
                  This finance model generates a yearly loss for you. Fix it by changing parameters.
                </div>
              </div>

              <v-card class="v-card--bordered">
                <v-card-text class="py-1 px-4">
                  <v-row
                    align="center"
                    no-gutters
                    class="list--bordered py-2"
                  >
                    <v-col
                      cols="12"
                      class="grey--text text--darken-2"
                    >
                      <div>Discount - enter absolute discount value</div>
                      <div class="caption grey--text">
                        Negative value will result in price increase
                      </div>
                    </v-col>
                    <v-col cols="4">
                      <currency-input
                        v-model="activeParameters.absoluteDiscount"
                        dense
                        label="Discount"
                        hide-details
                        :options="{ currency: null, precision: 0 }"
                        :suffix="yacht.currency"
                        :disabled="editingDisabled"
                        outlined
                        @change="onChangedAbsoluteDiscount"
                      />
                    </v-col>
                  </v-row>
                  <v-row
                    align="center"
                    no-gutters
                    class="list--bordered py-2"
                  >
                    <v-col class="pr-1">
                      <v-text-field
                        v-model="activeParameters.otherCostsDescription"
                        dense
                        label="Describe any other sales costs"
                        hide-details
                        :disabled="editingDisabled"
                        outlined
                        @change="onChangedAbsoluteDiscount"
                      />
                    </v-col>
                    <v-col cols="4">
                      <currency-input
                        v-model="activeParameters.otherCosts"
                        dense
                        label="Amount"
                        hide-details
                        :options="{ currency: null, precision: 0 }"
                        :suffix="yacht.currency"
                        :disabled="editingDisabled"
                        outlined
                        @change="onChangedAbsoluteDiscount"
                      />
                    </v-col>
                  </v-row>
                  <v-row
                    v-if="hasOperationalCostsPercentage"
                    align="center"
                    no-gutters
                    class="list--bordered py-2"
                  >
                    <v-col
                      cols="12"
                      class="grey--text text--darken-2"
                    >
                      <div>Yacht Operational Costs (default <strong>{{ yacht.default_yearly_cost_data ? yacht.default_yearly_cost_data.settings.operational.value : 0 }}%</strong>)</div>
                      <div class="caption grey--text">
                        % of yacht yearly income
                      </div>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field
                        v-model="activeParameters.operationalCostsPercentage"
                        dense
                        :placeholder="yacht.default_yearly_cost_data ? yacht.default_yearly_cost_data.settings.operational.value.toString() : null"
                        hide-details
                        suffix="%"
                        :disabled="editingDisabled"
                        outlined
                        @change="calculate()"
                      />
                    </v-col>
                  </v-row>
                  <v-row
                    v-if="hasFirstPaymentPercentage && firstPaymentPercentageEditable"
                    align="center"
                    no-gutters
                    class="list--bordered py-2"
                  >
                    <v-col
                      class="grey--text text--darken-2"
                    >
                      First Payment Percentage
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        v-if="!editingDisabled"
                        text
                        small
                        color="secondary"
                        @click="applySuggestedParams"
                      >
                        Apply Suggested
                      </v-btn>
                    </v-col>
                    <v-col
                      cols="3"
                      class="pr-1"
                    >
                      <v-text-field
                        v-model="activeParameters.firstPaymentPercentage"
                        dense
                        hide-details
                        suffix="%"
                        :disabled="editingDisabled"
                        outlined
                        @input="calculate()"
                      />
                    </v-col>
                    <v-col cols="4">
                      <currency-input
                        :value="calculator.data.current.investor.first_payment ? Math.round(calculator.data.current.investor.first_payment) : null"
                        dense
                        :options="{ currency: null, precision: 0 }"
                        label="First Payment"
                        hide-details
                        :suffix="yacht.currency"
                        :disabled="editingDisabled"
                        outlined
                        @change="onInputFirstPaymentAbsolute"
                      />
                    </v-col>
                  </v-row>
                  <v-row
                    v-if="hasLastPaymentPercentage && lastPaymentPercentageEditable"
                    align="center"
                    no-gutters
                    class="list--bordered py-2"
                  >
                    <v-col
                      class="grey--text text--darken-2"
                    >
                      Last Payment Percentage
                    </v-col>
                    <v-col
                      cols="3"
                      class="pr-1"
                    >
                      <v-text-field
                        v-model="activeParameters.lastPaymentPercentage"
                        dense
                        hide-details
                        suffix="%"
                        :disabled="editingDisabled"
                        outlined
                        @input="calculate()"
                      />
                    </v-col>
                    <v-col cols="4">
                      <currency-input
                        :value="calculator.data.current.investor.last_payment ? Math.round(calculator.data.current.investor.last_payment) : null"
                        dense
                        :options="{ currency: null, precision: 0 }"
                        label="Last Payment"
                        hide-details
                        :suffix="yacht.currency"
                        :disabled="editingDisabled"
                        outlined
                        @change="onInputLastPaymentAbsolute"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <v-subheader class="mt-3">
                Presented to Yacht owner
              </v-subheader>

              <v-card class="v-card--bordered">
                <v-card-text class="py-1 px-4">
                  <v-row
                    v-if="calculator.data.current.investor.sale_price"
                    align="center"
                    no-gutters
                    class="list--bordered py-2"
                  >
                    <v-col
                      cols="11"
                      class="grey--text text--darken-2"
                    >
                      Yacht Price
                    </v-col>
                    <v-col
                      cols="5"
                      class="text-right subtitle-1"
                    >
                      {{ calculator.data.current.investor.sale_price | currency(yacht.currency) }}
                    </v-col>
                  </v-row>

                  <v-row
                    v-if="calculator.data.current.investor.first_payment"
                    align="center"
                    no-gutters
                    class="list--bordered py-2"
                  >
                    <v-col
                      cols="11"
                      class="grey--text text--darken-2"
                    >
                      First Payment
                    </v-col>
                    <v-col
                      cols="5"
                      class="text-right subtitle-1"
                    >
                      {{ calculator.data.current.investor.first_payment_percentage | percentage }}
                      <span class="grey--text">
                        ({{ calculator.data.current.investor.first_payment | currency(yacht.currency) }})
                      </span>
                    </v-col>
                  </v-row>

                  <v-row
                    v-if="calculator.data.current.investor.last_payment"
                    align="center"
                    no-gutters
                    class="list--bordered py-2"
                  >
                    <v-col
                      cols="11"
                      class="grey--text text--darken-2"
                    >
                      Last Payment
                    </v-col>
                    <v-col
                      cols="5"
                      class="text-right subtitle-1"
                    >
                      {{ calculator.data.current.investor.last_payment_percentage | percentage }}
                      <span class="grey--text">
                        ({{ calculator.data.current.investor.last_payment | currency(yacht.currency) }})
                      </span>
                    </v-col>
                  </v-row>

                  <v-row
                    v-if="calculator.data.current.investor.investment_period_months"
                    align="center"
                    no-gutters
                    class="list--bordered py-2"
                  >
                    <v-col
                      cols="11"
                      class="grey--text text--darken-2"
                    >
                      Investment period
                    </v-col>
                    <v-col
                      cols="5"
                      class="text-right subtitle-1"
                    >
                      {{ calculator.data.current.investor.investment_period_months / 12 }} years
                    </v-col>
                  </v-row>

                  <v-row
                    v-if="calculator.data.current.investor.guaranteed_yearly_income"
                    align="center"
                    no-gutters
                    class="list--bordered py-2"
                  >
                    <v-col
                      cols="11"
                      class="grey--text text--darken-2"
                    >
                      Guaranteed yearly income
                    </v-col>
                    <v-col
                      cols="5"
                      class="text-right subtitle-1"
                    >
                      <span
                        v-if="calculator.data.current.investor.guaranteed_yearly_income_percentage"
                        class="grey--text"
                      >
                        ({{ calculator.data.current.investor.guaranteed_yearly_income_percentage | percentage }})
                      </span>
                      {{ calculator.data.current.investor.guaranteed_yearly_income | currency(yacht.currency) }}
                    </v-col>
                  </v-row>

                  <div v-if="calculator.data.current.investor.guaranteed_incomes">
                    <v-row
                      v-for="(yearsIncome, batch) in calculator.data.current.investor.guaranteed_incomes"
                      :key="'yearly_income_' + yearsIncome.start"
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        class="grey--text text--darken-2"
                      >
                        Guaranteed yearly income - years {{ yearsIncome.start }} - {{ yearsIncome.end }}
                      </v-col>
                      <v-col
                        cols="auto"
                        class="text-right subtitle-1"
                      >
                        <div
                          v-if="!editingCustomIncomes"
                          class="text-no-wrap"
                        >
                          <v-btn
                            v-if="!editingDisabled"
                            small
                            text
                            color="primary"
                            class="text-none ma-0 mr-2"
                            @click="editingCustomIncomes = true"
                          >
                            Edit
                          </v-btn>
                          <span
                            v-if="yearsIncome.guaranteed_yearly_income_percentage"
                            class="grey--text"
                          >
                            ({{ yearsIncome.guaranteed_yearly_income_percentage | percentage(1) }})
                          </span>
                          {{ yearsIncome.guaranteed_yearly_income | currency(yacht.currency) }}
                        </div>
                        <div
                          v-else-if="!editingDisabled"
                          class="text-no-wrap"
                        >
                          <div>
                            <currency-input
                              v-model="customIncomes[batch]"
                              dense
                              label="Income"
                              hide-details
                              style="max-width: 150px"
                              :suffix="yacht.currency"
                              :disabled="editingDisabled"
                              outlined
                            />
                          </div>
                          <div>
                            <v-btn
                              small
                              text
                              color="primary"
                              class="text-none ma-0 mr-2"
                              @click="saveCustomIncomes()"
                            >
                              Save
                            </v-btn>
                            <v-btn
                              small
                              text
                              color="error"
                              class="text-none ma-0 mr-2"
                              @click="clearCustomIncomes()"
                            >
                              Clear
                            </v-btn>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>

                  <v-row
                    v-if="calculator.data.current.investor.estimated_yearly_income"
                    align="center"
                    no-gutters
                    class="list--bordered py-2"
                  >
                    <v-col
                      cols="11"
                      class="grey--text text--darken-2"
                    >
                      Estimated yearly income
                    </v-col>
                    <v-col
                      cols="5"
                      class="text-right subtitle-1"
                    >
                      {{ calculator.data.current.investor.estimated_yearly_income | currency(yacht.currency) }}
                    </v-col>
                  </v-row>

                  <v-row
                    v-if="calculator.data.current.investor.estimated_yearly_expense"
                    align="center"
                    no-gutters
                    class="list--bordered py-2"
                  >
                    <v-col
                      cols="11"
                      class="grey--text text--darken-2"
                    >
                      Estimated yearly cost
                    </v-col>
                    <v-col
                      cols="5"
                      class="text-right subtitle-1"
                    >
                      {{ calculator.data.current.investor.estimated_yearly_expense | currency(yacht.currency) }}
                    </v-col>
                  </v-row>

                  <v-row
                    v-if="calculator.data.current.investor.estimated_yearly_net_income"
                    align="center"
                    no-gutters
                    class="list--bordered py-2"
                  >
                    <v-col
                      cols="11"
                      class="grey--text text--darken-2"
                    >
                      <strong>Estimated net yearly income</strong>
                    </v-col>
                    <v-col
                      cols="5"
                      class="text-right subtitle-1"
                    >
                      <strong>
                        <span
                          v-if="calculator.data.current.investor.estimated_yearly_net_income_percentage"
                          class="grey--text"
                        >
                          ({{ calculator.data.current.investor.estimated_yearly_net_income_percentage | percentage(1) }})
                        </span>
                        {{ calculator.data.current.investor.estimated_yearly_net_income | currency(yacht.currency) }}
                      </strong>
                    </v-col>
                  </v-row>

                  <div v-if="calculator.data.current.investor.additional_income">
                    <v-row
                      v-for="income in calculator.data.current.investor.additional_income"
                      :key="income.year"
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Guaranteed income year {{ income.year }}
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        <span class="grey--text">
                          ({{ income.guaranteed_income_percentage | percentage(1) }})
                        </span>
                        {{ income.guaranteed_income | currency(yacht.currency) }}
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>
              </v-card>

              <div v-if="$can('view-final-margin', 'Yacht')">
                <v-subheader class="mt-3">
                  You (Company)
                </v-subheader>

                <v-card class="v-card--bordered">
                  <v-card-text class="py-1 px-4">
                    <v-row
                      v-if="calculator.data.current.navigare.sale_price"
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        {{ calculator.data.current.navigare.type === 'lease' ? 'Financed Amount' : 'Sale Price' }}
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        {{ calculator.data.current.navigare.sale_price | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      v-if="calculator.data.current.navigare.financing_amount"
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Financed Amount (incl. down payment)
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        {{ calculator.data.current.navigare.financing_amount | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      v-if="calculator.data.current.navigare.purchase_price"
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Purchase Price
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        {{ calculator.data.current.navigare.purchase_price | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      v-if="calculator.data.current.navigare.other_costs"
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Other Costs: {{ calculator.data.current.navigare.other_costs_description }}
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        {{ calculator.data.current.navigare.other_costs | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      v-if="calculator.data.current.navigare.down_payment"
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Down Payment
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        <span class="grey--text">
                          ({{ calculator.data.current.navigare.down_payment_percentage | percentage }})
                        </span>
                        {{ calculator.data.current.navigare.down_payment | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      v-if="calculator.data.current.navigare.last_payment"
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Last Payment
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        <span class="grey--text">
                          ({{ calculator.data.current.navigare.last_payment_percentage | percentage }})
                        </span>
                        {{ calculator.data.current.navigare.last_payment | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      v-if="calculator.data.current.navigare.insurance"
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Insurance
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        <span class="grey--text">
                          ({{ calculator.data.current.navigare.insurance_percentage | percentage }})
                        </span>
                        {{ calculator.data.current.navigare.insurance | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      v-if="calculator.data.current.navigare.support_amount"
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Support to cover yearly loss
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        {{ calculator.data.current.navigare.support_amount | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      v-if="calculator.data.current.navigare.monthly_payment"
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Monthly Lease Annuity
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        {{ calculator.data.current.navigare.monthly_payment | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Estimated Yearly Income
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        {{ calculator.data.current.navigare.estimated_yearly_income | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Estimated Yearly Expense
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        {{ calculator.data.current.navigare.estimated_yearly_expense | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      v-if="calculator.data.current.navigare.estimated_net_yearly_income"
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Estimated Net Yearly Income
                        <br>
                        <div class="caption grey--text">
                          Adjusted for income decrease during investment period
                        </div>
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        {{ calculator.data.current.navigare.estimated_net_yearly_income | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Average Yearly Profit<br>
                        <div class="caption grey--text">
                          Net yearly yacht income - financing payments
                        </div>
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        {{ calculator.data.current.navigare.profit.yearly_profit | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      align="center"
                      no-gutters
                      class="list--bordered py-2"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Initial Profit<br>
                        <div class="caption grey--text">
                          First payment from investor/leasing - any initial expenses
                        </div>
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1"
                      >
                        {{ calculator.data.current.navigare.profit.initial | currency(yacht.currency) }}
                      </v-col>
                    </v-row>

                    <v-row
                      align="center"
                      no-gutters
                      class="list--bordered py-2 font-weight-bold"
                    >
                      <v-col
                        cols="11"
                        class="grey--text text--darken-2"
                      >
                        Total Profit
                      </v-col>
                      <v-col
                        cols="5"
                        class="text-right subtitle-1 font-weight-bold"
                      >
                        {{ calculator.data.current.navigare.profit.total_profit | currency(yacht.currency) }}
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </v-expand-transition>
        </v-col>
        <!--<editor-fold desc="Price Column">-->
        <v-col
          lg="5"
          class="pa-6 fill-height"
          style="overflow-y: auto"
        >
          <v-subheader>
            Yacht Annex Price
          </v-subheader>

          <small-pricing-list
            :condensed="true"
            :base-price="yacht.base_sale_price"
            :base-currency="yacht.model.currency"
            :equipment-price="yacht.equipment_sale_price"
            :cdr-cost="yacht.cdr_price"
            :currency="yacht.currency"
            :production-year-price-increase="yacht.production_year_price_increase"
            :total-price="yacht.total_sale_price"
          />
        </v-col>
        <!--</editor-fold>-->
      </v-row>
    </v-card-text>

    <template slot="dialogActions">
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="submitting"
        @click="storeFinance"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
  </content-dialog>
</template>

<script>
import ContentDialog from '@/components/helpers/Dialog'
import FinanceModelsApi from '@/services/api/FinanceModels'
import Yacht from '@/models/Yacht'
import SmallPricingList from '@/components/business/yachts/SmallPricingList'
import Big from 'big.js'
import CurrencyInput from '@/components/helpers/CurrencyInput'

export default {
    components: {
      CurrencyInput,
      SmallPricingList,
      ContentDialog
    },
    props: {
      visible: {
        type: Boolean,
        required: true
      },
      yacht: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        selectedFinanceModel: null,
        disableSettingDefaultParams: false,
        activeParameters: {
          firstPaymentPercentage: 0,
          lastPaymentPercentage: 0,
          discount: 0,
          absoluteDiscount: 0,
          otherCosts: 0,
          otherCostsDescription: '',
          operationalCostsPercentage: null
        },
        customIncomes: [],
        editingCustomIncomes: false,
        financeModels: {
          loading: false,
          data: []
        },
        calculator: {
          loading: false,
          data: {
            current: null,
            suggestion: null,
            defaultParams: null
          }
        },
        submitting: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      editingDisabled() {
        return !this.yacht.editable_fields.includes('finance_model')
      },
      hasFirstPaymentPercentage() {
        return this.selectedFinanceModel && ('firstPaymentPercentage' in this.selectedFinanceModel.parameters)
      },
      hasLastPaymentPercentage() {
        return this.selectedFinanceModel && ('lastPaymentPercentage' in this.selectedFinanceModel.parameters)
      },
      firstPaymentPercentageEditable() {
        if (!this.selectedFinanceModel || !this.selectedFinanceModel.specifications || !this.selectedFinanceModel.specifications.parameters) return false

        return this.selectedFinanceModel.specifications.parameters.find(p => p.field === 'firstPaymentPercentage').editable
      },
      lastPaymentPercentageEditable() {
        if (!this.selectedFinanceModel || !this.selectedFinanceModel.specifications || !this.selectedFinanceModel.specifications.parameters) return false

        return this.selectedFinanceModel.specifications.parameters.find(p => p.field === 'lastPaymentPercentage').editable
      },
      hasOperationalCostsPercentage() {
        return this.selectedFinanceModel && this.selectedFinanceModel.specifications && this.selectedFinanceModel.specifications.has_editable_operational_costs
      }
    },
    watch: {
      yacht: function () {
        this.loadYachtFinanceModel()
      },
      visible: function (value) {
        if (!value) return

        this.loadYachtFinanceModel()
      }
    },
    created() {
      this.fetchFinanceModels()
    },
    methods: {
      onChangedFinanceModel(value) {
        if (!value) {
          this.clearFinancing()
          return
        }

        this.setFinanceModelParameters()
        this.calculate()
      },
      onChangedAbsoluteDiscount() {
        this.$nextTick(() => {
          this.calculate()
        })
      },
      setFinanceModelParameters() {
        if (this.editingDisabled || !this.selectedFinanceModel || this.disableSettingDefaultParams) return

        if (this.hasFirstPaymentPercentage) {
          this.activeParameters.firstPaymentPercentage = Big(this.selectedFinanceModel.parameters.firstPaymentPercentage).times(100).toString()
        }

        if (this.hasLastPaymentPercentage) {
          this.activeParameters.lastPaymentPercentage = Big(this.selectedFinanceModel.parameters.lastPaymentPercentage).times(100).toString()
        }
      },
      loadYachtFinanceModel() {
        if (!this.yacht.finance_model) return

        this.loadSavedCalculation()

        this.disableSettingDefaultParams = true

        if ('firstPaymentPercentage' in this.yacht.finance_model_params) {
          this.activeParameters.firstPaymentPercentage = Big(this.yacht.finance_model_params.firstPaymentPercentage).times(100).toString()
        }
        if ('lastPaymentPercentage' in this.yacht.finance_model_params) {
          this.activeParameters.lastPaymentPercentage = Big(this.yacht.finance_model_params.lastPaymentPercentage).times(100).toString()
        }
        if (this.yacht.yearly_cost_data && this.yacht.yearly_cost_data.operational) {
          this.activeParameters.operationalCostsPercentage = Big(this.yacht.yearly_cost_data.operational).toString()
        }
        this.activeParameters.absoluteDiscount = Big(this.yacht.total_sale_price).minus(this.yacht.total_sale_price_final).toString()

        this.setCustomIncomes(this.yacht.finance_model_calculation.investor.guaranteed_incomes)

        // disableSettingDefaultParams will prevent this change to set default parameters
        this.selectedFinanceModel = this.yacht.finance_model

        this.$nextTick(() => {
          this.disableSettingDefaultParams = false
        })
      },
      async calculate() {
        if (this.editingDisabled) {
          this.loadSavedCalculation()
          return
        }

        this.calculator.loading = true

        try {
          let result = await Yacht.api.calculateFinance(this.yacht.id, this.selectedFinanceModel.id, this.getParameters())

          this.calculator.data.current = result.calculation
          this.calculator.data.suggestion = result.suggestion

          this.setCustomIncomes(result.calculation.investor.guaranteed_incomes)
        } catch (e) {
          throw e
        } finally {
          this.calculator.loading = false
        }
      },
      loadSavedCalculation() {
        if (!this.yacht.finance_model) return

        this.calculator.data.current = this.yacht.finance_model_calculation

        this.setCustomIncomes(this.yacht.finance_model_calculation.investor.guaranteed_incomes)
      },
      setCustomIncomes(guaranteedIncomesFromCalculation) {
        if (!guaranteedIncomesFromCalculation) return

        this.customIncomes = guaranteedIncomesFromCalculation.map(i => i.guaranteed_yearly_income)
      },
      applySuggestedParams() {
        this.activeParameters.firstPaymentPercentage = Big(this.calculator.data.suggestion.firstPaymentPercentage).times(100).round().toString()

        this.calculate()
      },
      async fetchFinanceModels() {
        this.financeModels.loading = true

        try {
          this.financeModels.data = await FinanceModelsApi.getForCountry(this.yacht.country_id)
        } catch (e) {
          throw e
        } finally {
          this.financeModels.loading = false
        }
      },
      async storeFinance() {
        this.submitting = true

        try {
          const response = await Yacht.api.storeFinance(this.yacht.id, this.selectedFinanceModel ? this.selectedFinanceModel.id : null, this.selectedFinanceModel ? this.getParameters() : null)

          this.$store.dispatch('alert', {
            text: 'Price and financing model successfully updated',
            color: 'success'
          })

          this.modalVisible = false

          this.$emit('update:yacht', response.yacht)
        } catch (e) {
          throw e
        } finally {
          this.submitting = false
        }
      },
      getParameters() {
        let parameters = {
          discount: isFinite(Number(this.activeParameters.absoluteDiscount)) ? this.activeParameters.absoluteDiscount : 0,
          otherCosts: isFinite(Number(this.activeParameters.otherCosts)) ? this.activeParameters.otherCosts : 0,
          otherCostsDescription: this.activeParameters.otherCostsDescription
        }

        if (this.hasFirstPaymentPercentage) {
          parameters.firstPaymentPercentage = Big(this.activeParameters.firstPaymentPercentage).div(100).toString()
        }

        if (this.hasFirstPaymentPercentage) {
          parameters.lastPaymentPercentage = Big(this.activeParameters.lastPaymentPercentage).div(100).toString()
        }

        if (this.customIncomes && this.customIncomes.length > 0) {
          parameters.customIncomes = this.customIncomes
        }

        if (this.hasOperationalCostsPercentage && this.activeParameters.operationalCostsPercentage && this.activeParameters.operationalCostsPercentage > 0) {
          parameters.operationalCostsPercentage = Big(this.activeParameters.operationalCostsPercentage).div(100).toString()
        }

        return parameters
      },
      saveCustomIncomes() {
        this.editingCustomIncomes = false

        this.calculate()
      },
      clearCustomIncomes() {
        this.customIncomes = []
        this.editingCustomIncomes = false

        this.calculate()
      },
      clearFinancing() {
        this.calculator.data.current = null
        this.calculator.data.defaultParams = null
        this.calculator.data.suggestion = null
      },
      onInputLastPaymentAbsolute(value) {
        let percentage = Big(value / (this.yacht.total_sale_price - this.activeParameters.absoluteDiscount)).times(100).round(4)

        this.activeParameters.lastPaymentPercentage = percentage.toString()

        this.calculate()
      },
      onInputFirstPaymentAbsolute(value) {
        let percentage = Big(value / (this.yacht.total_sale_price - this.activeParameters.absoluteDiscount)).times(100).round(4)

        this.activeParameters.firstPaymentPercentage = percentage.toString()

        this.calculate()
      }
    }
  }
</script>
